import React, { useState, useEffect } from 'react';

/**
 * Performance Badge - Shows API latency metrics in dev mode
 * Displays in bottom-right corner when REACT_APP_SHOW_PERF=true
 */
export function PerformanceBadge() {
  const [metrics, setMetrics] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  
  // Only show in dev mode or when explicitly enabled
  const showPerf = process.env.NODE_ENV === 'development' || 
                   process.env.REACT_APP_SHOW_PERF === 'true';
  
  useEffect(() => {
    if (!showPerf) return;
    
    // Intercept fetch to capture timing
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const url = args[0];
      const start = performance.now();
      
      try {
        const response = await originalFetch(...args);
        const end = performance.now();
        const duration = Math.round(end - start);
        
        // Only track API calls
        if (typeof url === 'string' && url.includes('/api/')) {
          const headers = {};
          response.headers.forEach((value, key) => {
            if (key.toLowerCase().startsWith('x-')) {
              headers[key.toLowerCase()] = value;
            }
          });
          
          setMetrics({
            endpoint: url.split('?')[0].replace(/.*\/api/, '/api'),
            totalMs: duration,
            dbMs: parseInt(headers['x-db-time']) || 0,
            queryCount: parseInt(headers['x-query-count']) || 0,
            requestId: headers['x-request-id'] || ''
          });
          setLastUpdate(new Date());
          setIsVisible(true);
          
          // Auto-hide after 5 seconds
          setTimeout(() => setIsVisible(false), 5000);
        }
        
        return response;
      } catch (error) {
        throw error;
      }
    };
    
    return () => {
      window.fetch = originalFetch;
    };
  }, [showPerf]);
  
  if (!showPerf || !metrics || !isVisible) return null;
  
  const getColor = (ms) => {
    if (ms < 200) return 'text-green-400';
    if (ms < 500) return 'text-yellow-400';
    return 'text-red-400';
  };
  
  return (
    <div 
      className="fixed bottom-4 right-4 z-50 bg-gray-900/95 text-white text-xs font-mono rounded-lg shadow-lg border border-gray-700 p-2 max-w-[200px]"
      onClick={() => setIsVisible(false)}
      title="Click to dismiss"
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="text-gray-400">⚡</span>
        <span className="truncate text-gray-300">{metrics.endpoint}</span>
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
        <span className="text-gray-500">API:</span>
        <span className={getColor(metrics.totalMs)}>{metrics.totalMs}ms</span>
        
        <span className="text-gray-500">DB:</span>
        <span className={getColor(metrics.dbMs)}>{metrics.dbMs}ms</span>
        
        <span className="text-gray-500">Queries:</span>
        <span className={metrics.queryCount > 5 ? 'text-yellow-400' : 'text-green-400'}>
          {metrics.queryCount}
        </span>
      </div>
    </div>
  );
}

export default PerformanceBadge;
